import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Table, Button, Card, Input, Avatar, Popconfirm, Typography, Row, Col, Tooltip, Drawer, Select, message, Dropdown } from "antd";
import type { MenuProps } from "antd";
import { ColumnsType } from "antd/es/table";
import { SearchOutlined, FileExcelOutlined, DownOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import CompanyCreate from "../../components/Companies/CompanyCreate";
import CompanyService from "../../services/CompanyService";
import TrashIcon from '../../assets/icons/trash.svg';
import PenIcon from '../../assets/icons/pen.svg';
import { capitalizeWords, formatDate, hasRole, normalizeTurkish, openNotification } from "../../utils";
import { debounce } from 'lodash';
import './Company.scss';
import UserService from "../../services/UserService";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import AuthorizedPersonService from "../../services/AuthorizedPersonService";
import PhoneNumberDisplay from '../../components/PhoneNumberDisplay';
import ReminderDrawer, { ReminderFormValues } from "../../components/Reminders/ReminderDrawer";
import ReminderService from "../../services/ReminderService";
import { TaskDaily01Icon } from 'hugeicons-react';

const { Title, Text } = Typography;

interface Company {
  id: string;
  name: string;
  authorizedPersons: [
    {
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      default?: boolean;
    }
  ];
  email: string;
  phoneNumber: string;
  creationDate: string;
  imageId?: string;
  logoUrl?: string;
  account?: {
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
  };
}

const Companies: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const storedPermissions = localStorage.getItem("permissions");
  const [permissions, setPermissions] = useState<string[]>(JSON.parse(storedPermissions ?? '')); // company.show kontrolü
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [customerList, setCustomerList] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCentralUser, setIsCentralUser] = useState(false);
  const [filter, setFilter] = useState(0);
  const [loading, setLoading] = useState(false);

  const [districts, setDistricts] = useState<{ id: number; name: string }[]>([]);
  const [cities, setCities] = useState<{ id: number, name: string }[]>([]);

  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(null);

  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const cityIdsToShow = [2170, 2195, 2150, 2167, 2151, 2176, 2163, 2218, 2175];
  const [reminderDrawerOpen, setReminderDrawerOpen] = useState(false);
  const [reminderInitialValues, setReminderInitialValues] = useState<Partial<ReminderFormValues> | undefined>(undefined);
  const [reminderCompanyName, setReminderCompanyName] = useState<string | undefined>(undefined);
  const [exporting, setExporting] = useState(false);

  const handleCustomerChange = (value: number) => {
    setSelectedCustomerId(value);
  };

  const roles = JSON.parse(localStorage.getItem("roles") || "[]");

  const hasRequiredRole = roles.some(
    (role: any) => role.name === "ADMIN" || role.name === "CENTRAL_USER"
  );
  const isFinanceUser = useMemo(() => hasRole('FINANCE'), []);

  const isFinance = useMemo(() => hasRole('FINANCE'), []);
  const isCustomer = useMemo(() => hasRole('CUSTOMER'), []);
  const canViewAllCompanies = useMemo(
    () => permissions.includes('company.show') || isFinance,
    [permissions, isFinance],
  );

  const filterClear = () => {
    setSelectedCompanyId(null);
    setSelectedCustomerId(null);
    setSelectedCityId(null);
    setSelectedDistrictId(null);
    setCurrentPage(0);
    setFilter((prev) => prev + 1);
  };

  const showDrawer = () => {
    setIsDrawerVisible(true);
  };

  const showFilterDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const getCustomerAll = () => {
    UserService.getAllCustomer()
      .then((response: any) => {
        setCustomerList(response.data);
      })
      .catch((error) => console.error('Statüler yüklenirken hata oluştu:', error));
  }

  const getFilterProposal = () => {
    setFilter(filter + 1);
    onClose();
  };

  const handleOpenReminderDrawer = (company: any) => {
    const idNumber = Number(company?.id);
    setReminderInitialValues({
      activityType: 'CALL',
      entityTab: 'COMPANY',
      companyId: Number.isNaN(idNumber) ? undefined : idNumber,
    });
    setReminderCompanyName(company?.name || company?.title || company?.id?.toString());
    setReminderDrawerOpen(true);
  };

  const handleReminderSubmit = async (values: ReminderFormValues) => {
    try {
      const entityId = values.companyId;
      const isMeeting = values.activityType === 'MEETING_OFFLINE' || values.activityType === 'MEETING_ONLINE';
      const channelDefault = values.activityType === 'MEETING_OFFLINE' ? 'IN_PERSON' : values.activityType === 'MEETING_ONLINE' ? 'ONLINE' : '';
      const resolvedChannel = values.channel ?? (isMeeting ? channelDefault : undefined);
      const reminderDate = values.reminderDate
        ? values.reminderDate.format('YYYY-MM-DDTHH:mm:ss')
        : undefined;
      const dueDate = values.dueDate ? values.dueDate.format('YYYY-MM-DDTHH:mm:ss') : undefined;
      const properties =
        values.properties && typeof values.properties === 'object'
          ? JSON.stringify(values.properties)
          : values.properties;
      const payload = {
        content: values.content,
        reminderDate,
        dueDate,
        estimatedDurationMinutes: values.estimatedDurationMinutes,
        activityType: values.activityType,
        channel: resolvedChannel,
        link: isMeeting ? values.link : undefined,
        state: 'PENDING' as const,
        ...(values.statusId ? { statusId: values.statusId } : {}),
        ...(properties ? { properties } : {}),
        ...(entityId ? { entityName: 'COMPANY', entityId } : {}),
      };

      if (isAdmin) {
        await ReminderService.createForAccount({ ...payload, accountId: values.accountId });
      } else {
        await ReminderService.createForMe(payload);
      }

      openNotification('success', 'Başarılı', 'Görev oluşturuldu');
      setReminderDrawerOpen(false);
      setReminderInitialValues(undefined);
    } catch (e: any) {
      if (e?.errorFields) return;
      openNotification('error', 'Hata', e?.response?.data || 'Görev oluşturulamadı');
    }
  };

  const userId = localStorage.getItem("id");
  const isPartner = localStorage.getItem("isPartner");

  const navigate = useNavigate();
  const location = useLocation();

  // Arama (debounce)
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.length >= 1) {
        setSearchTerm(value);
        setCurrentPage(0);
      } else {
        setSearchTerm("");
        setCurrentPage(0);
      }
    }, 300),
    []
  );

  const handleDistrictChange = (value: number) => {
    setSelectedDistrictId(value);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLocaleLowerCase("tr-TR");
    debouncedSearch(value);
  };

  const handleExportAuthorizedPersons = async () => {
    try {
      const response = await AuthorizedPersonService.getAll();
      const rawData = response?.data ?? [];
      const authorizedPersons = Array.isArray(rawData) ? rawData : [];

      const headerRow: any[] = [
        { v: 'Ad', s: { font: { bold: true } } },
        { v: 'Soyad', s: { font: { bold: true } } },
        { v: 'Unvan', s: { font: { bold: true } } },
        { v: 'E-Posta', s: { font: { bold: true } } },
        { v: 'Telefon', s: { font: { bold: true } } },
        { v: 'Birincil', s: { font: { bold: true } } },
        { v: 'Firma Adı', s: { font: { bold: true } } },
      ];

      const rows = authorizedPersons.map((person: any) => {
        const isDefault = typeof person.isDefault === 'boolean'
          ? person.isDefault
          : person?.default;

        return [
          person.firstName ?? '',
          person.lastName ?? '',
          person.position ?? '',
          person.email ?? '',
          person.phoneNumber ?? '',
          isDefault ? 'Evet' : 'Hayır',
          person.companyName ?? '',
        ];
      });

      const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Yetkili Kişiler');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
      saveAs(blob, 'yetkili-kisiler.xlsx');
    } catch (error) {
      console.error('Excel dışa aktarımında hata:', error);
      message.error('Excel dışa aktarımı başarısız oldu');
    }
  };

  const handleExportCompanies = async () => {
    try {
      setExporting(true);

      let selectedCity: any = '';
      if (selectedCityId) {
        selectedCity = cities.find((city) => city.id === selectedCityId);
      }

      let selectedDistrict: any = '';
      if (selectedDistrictId) {
        selectedDistrict = districts.find((district) => district.id === selectedDistrictId);
      }

      const fetchFn = canViewAllCompanies ? CompanyService.getAll : CompanyService.getAllMe;

      const response = await fetchFn({
        search: normalizeTurkish(searchTerm),
        city: selectedDistrictId === null ? (selectedCity ? normalizeTurkish(selectedCity?.name) : "") : "",
        district: selectedDistrict ? normalizeTurkish(selectedDistrict?.name) : "",
        accountId: selectedCustomerId ? selectedCustomerId : "",
        page: 0,
        size: 10000,
        sortField,
        sortDirection,
      });

      const list = Array.isArray(response?.data?.content) ? response.data.content : [];

      if (!list.length) {
        message.info('Aktarılacak firma bulunamadı');
        setExporting(false);
        return;
      }

      const headerRow: any[] = [
        { v: 'Firma Adı', s: { font: { bold: true } } },
        { v: 'İl', s: { font: { bold: true } } },
        { v: 'İlçe', s: { font: { bold: true } } },
        { v: 'Adres', s: { font: { bold: true } } },
        { v: 'Telefon Numarası', s: { font: { bold: true } } },
        { v: 'E-posta Adresi', s: { font: { bold: true } } },
        { v: 'KEP Adresi', s: { font: { bold: true } } },
        { v: 'Vergi Dairesi', s: { font: { bold: true } } },
        { v: 'Vergi Numarası', s: { font: { bold: true } } },
        { v: 'Kayıt Tarihi', s: { font: { bold: true } } },
        { v: 'Kayıt Eden Kullanıcı Adı Soyadı', s: { font: { bold: true } } },
      ];

      const rows = list.map((company: any) => {
        const addresses = Array.isArray(company.addresses)
          ? company.addresses
          : Array.isArray(company.companyAddresses)
            ? company.companyAddresses
            : [];
        const billingAddress = addresses.find((addr: any) => addr?.type === 'BILLING') || addresses[0] || {};

        const cityName = billingAddress?.city || '';
        const districtName = billingAddress?.state || '';
        const addressLine = billingAddress?.addressLine1 || '';
        const phone = company.phoneNumber || billingAddress?.phone || '';
        const createdAt = formatDate(company.createdAt || company.creationDate);
        const accountName = `${company?.account?.firstName ?? ''} ${company?.account?.lastName ?? ''}`.trim();

        return [
          company.name || '',
          cityName,
          districtName,
          addressLine,
          phone,
          company.email || '',
          company.kepAddress || '',
          company.taxOffice || '',
          company.taxNumber || '',
          createdAt,
          accountName,
        ];
      });

      const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Firmalar');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
      saveAs(blob, 'firmalar.xlsx');
    } catch (error) {
      console.error('Firma Excel dışa aktarılırken hata oluştu:', error);
      message.error('Excel dışa aktarımı başarısız oldu');
    } finally {
      setExporting(false);
    }
  };

  const exportMenuItems: MenuProps['items'] = [
    {
      key: 'companies',
      label: 'Firma',
      onClick: () => handleExportCompanies(),
    },
    {
      key: 'authorizedPersons',
      label: 'Yetkili Kişiler',
      onClick: () => handleExportAuthorizedPersons(),
    },
  ];

  const getCities = () => {
    CompanyService.getStates(225)
      .then((response: any) => {
        setCities(response.data);
      })
  }

  useEffect(() => {

    const isUserAdmin = hasRole('ADMIN');
    setIsAdmin(isUserAdmin);

    const isUserCentralUser = hasRole('CENTRAL_USER');
    setIsCentralUser(isUserCentralUser);

    if (isUserAdmin || isUserCentralUser) {
      getCustomerAll();
    }

    getCities();

  }, []);

  // Şirketleri çek
  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerVisible, searchTerm, currentPage, pageSize, sortField, sortDirection, filter]);

  const fetchCompanies = async () => {

    setLoading(true);
    try {
      // Eğer "company.show" izni varsa => getAll
      // yoksa => getAllMe

      let selectedCity: any = "";
      if (selectedCityId) {
        selectedCity = cities.find(city => city.id === selectedCityId);
      }

      let selectedDistrict: any = "";
      if (selectedDistrictId) {
        selectedDistrict = districts.find(district => district.id === selectedDistrictId);
      }


      let fetchFn;
      console.log(permissions);
      console.log(canViewAllCompanies);
      if (canViewAllCompanies) {
        fetchFn = CompanyService.getAll;
      } else {
        fetchFn = CompanyService.getAllMe;
      }

      let selectedCityName = selectedDistrictId === null ? (selectedCity ? normalizeTurkish(selectedCity?.name) : "") : "";

      const response = await fetchFn({
        search: normalizeTurkish(searchTerm),
        city: selectedCityName,
        district: selectedDistrict ? normalizeTurkish(selectedDistrict?.name) : "",
        accountId: selectedCustomerId ? selectedCustomerId : "",
        page: currentPage,
        size: pageSize,
        sortField,
        sortDirection
      });
      setLoading(false);
      setCompanies(response.data.content);
      setFilteredCompanies(response.data.content);
      setTotal(response.data.totalElements);

    } catch (error) {
      setLoading(false);
      console.error("Şirketler yüklenirken hata oluştu:", error);
    }
  };

  // Tablo sayfa/sort değişimi
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    setCurrentPage(pagination.current - 1);
    setPageSize(pagination.pageSize || 50);
    setSortField(sorter.field || "createdAt");
    setSortDirection(sorter.order === "ascend" ? "ASC" : "DESC");
  };

  const getDistricts = async (stateId: number) => {
    try {
      const response = await CompanyService.getDistricts(stateId);
      setDistricts(response.data);
    } catch (error) {
      console.error("İlçeler alınırken hata oluştu:", error);
    }
  };

  // URL query param check
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const activeKey = queryParams.get("activeKey");
    if (activeKey === "create") {
      showDrawer();
    }
  }, [location.search]);

  // Teklifle ilgili navigasyon
  const handleOfferNavigation = (companyId: any) => {
    navigate(`/proposals/create?companyId=${companyId}`);
  };

  // Company detail
  const handleSendOffer = (companyId: string) => {
    navigate(`/companies/${companyId}`);
  };


  const handleCityChange = (value: number) => {
    const city = cities.find((c) => c.id === value);
    setSelectedCityId(value);
    setSelectedDistrictId(null);
    setDistricts([]);
    if (value) {
      getDistricts(value);
    }
  };

  // Firma silme
  const handleDelete = async (companyId: string) => {
    try {
      await CompanyService.remove(companyId);
      openNotification("success", "Başarılı", "Firma başarıyla silindi");
      const updatedCompanies = companies.filter((c) => c.id !== companyId);
      setCompanies(updatedCompanies);
      setFilteredCompanies(updatedCompanies);
    } catch (error) {
      openNotification("error", "Hata", "Firma silinirken bir hata oluştu");
    }
  };


  const closeDrawer = () => {
    setIsDrawerVisible(false);
  };

  // Tablo kolonları
  const columns: ColumnsType<Company> = [
    {
      title: "Firma Adı",
      dataIndex: "name",
      key: "name",
      render: (text, record) => {
        const displayText = text.length > 28 ? `${text.substring(0, 28)}...` : text;
        return (
          <div className="d-flex align-items-center">
            <div className="avatar-image-logo" style={{ marginRight: 10 }}>
              <Avatar
                className="avatar-image-logo"
                style={{ backgroundColor: "#f0f0f0", color: "#000" }}
              >
                {record.name.charAt(0).toUpperCase()}
              </Avatar>
            </div>
            <Tooltip title={text.toUpperCase()}>
              <span>{displayText.toUpperCase()}</span>
            </Tooltip>
          </div>
        );
      },
    },
    ...(canViewAllCompanies
      ? [
        {
          title: "Kullanıcı Ad Soyad",
          key: "accountOwner",
          render: (text: any, record: any) =>
            record.account
              ? `${record.account.firstName} ${record.account.lastName}`
              : "Bilgi Yok",
        },
        {
          title: "Kullanıcı E-posta",
          key: "accountEmail",
          render: (text: any, record: any) =>
            record.account ? record.account.email : "Bilgi Yok",
        },
        {
          title: "Kullanıcı Telefon No",
          key: "accountPhone",
          render: (_text: any, record: any) =>
            record.account ? <PhoneNumberDisplay value={record.account.phoneNumber} /> : "Bilgi Yok",
        },
      ]
      : [
        {
          title: "Yetkili Ad Soyad",
          key: "authorizedPersons",
          render: (text: any, record: any) => {
            const defaultPerson =
              record.authorizedPersons.find((p: any) => p.default) ||
              record.authorizedPersons[0];
            return defaultPerson
              ? `${capitalizeWords(defaultPerson.firstName)} ${capitalizeWords(
                defaultPerson.lastName
              )}`
              : "Yetkili Bulunamadı";
          },
        },
        {
          title: "Yetkili E-posta",
          dataIndex: "email",
          key: "email",
          render: (text: any, record: any) => {
            const defaultPerson =
              record.authorizedPersons.find((p: any) => p.default) ||
              record.authorizedPersons[0];
            return defaultPerson ? defaultPerson.email : "Yetkili E-posta Bulunamadı";
          },
        },
        {
          title: "Yetkili Telefon",
          dataIndex: "phoneNumber",
          key: "phoneNumber",
          render: (_text: any, record: any) => {
            const defaultPerson =
              record.authorizedPersons.find((p: any) => p.default) ||
              record.authorizedPersons[0];
            return defaultPerson ? (
              <PhoneNumberDisplay value={defaultPerson.phoneNumber} />
            ) : (
              "Yetkili Telefon Bulunamadı"
            );
          },
        },
      ]),
    {
      title: "Kayıt Tarihi",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (text) => <Text>{formatDate(text)}</Text>,
    },
    {
      title: "İşlem",
      key: "action",
      align: "center",
      width: 260,
      render: (text, record: any) => {
        const hasRequiredRole = record?.account?.roles?.some(
          (role: any) =>
            role.name === "ADMIN" || role.name === "CENTRAL_USER"
        );
        const ownerId = record?.account?.id;
        const isOwnedCompany =
          ownerId !== undefined &&
          ownerId !== null &&
          userId !== null &&
          String(ownerId) === String(userId);
        const shouldDisableCreateOffer = permissions.includes('company.show')
          ? !isOwnedCompany
          : !isOwnedCompany || (!isCustomer && isPartner === "false");
        return (
          <div className="companies-action">
            {!isFinanceUser && (
              <Tooltip
                title={
                  isPartner?.toString() === "false"
                    ? "Teklif oluşturabilmek için profil sayfanızda yer alan Sözleşme bölümündeki İş Ortaklığı Sözleşmesini onaylamanız gerekmektedir."
                    : ""
                }
              >
                {hasRequiredRole ? (
                  <Button
                    type="default"
                    className="action-button"
                    onClick={() => handleOfferNavigation(record.id)}
                    disabled={!hasRequiredRole}
                  >
                    Teklif Oluştur
                  </Button>
                ) : (
                  <Button
                    type="default"
                    className="action-button"
                    onClick={() => handleOfferNavigation(record.id)}
                    disabled={shouldDisableCreateOffer}
                  >
                    Teklif Oluştur
                  </Button>
                )}
              </Tooltip>
            )}
            {(isAdmin || isCentralUser) && !isCustomer && (
              <Tooltip title="Görev Oluştur">
                <Button
                  type="default"
                  className="action-button icon-action-button"
                  shape="circle"
                  onClick={() => handleOpenReminderDrawer(record)}
                  style={{ marginLeft: 8 }}
                >
                  <TaskDaily01Icon style={{ fontSize: 20 }} />
                </Button>
              </Tooltip>
            )}
            <Button
              icon={<img src={PenIcon} alt="pen" className="icon" />}
              shape="circle"
              onClick={() => handleSendOffer(record.id)}
              className="icon-button ml-12px"
            />
            {isAdmin && (
              <Popconfirm
                placement="left"
                title="Bu firmayı silmek istediğinize emin misiniz?"
                onConfirm={() => handleDelete(record.id)}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button
                  icon={<img src={TrashIcon} alt="trash" className="icon" />}
                  shape="circle"
                  className="icon-button ml-12px"
                />
              </Popconfirm>
            )}
          </div>);
      },
    },
  ];

  const pagination = useMemo(
    () => ({
      current: currentPage + 1,
      pageSize: pageSize,
      total: total,
      onChange: (page: number, pageSize?: number) => {
        setCurrentPage(page - 1);
        setPageSize(pageSize || 50);
      },
      showSizeChanger: true,
      pageSizeOptions: ["50", "100", "200", "500"],
      defaultPageSize: 50,
    }),
    [currentPage, pageSize, total]
  );

  return (
    <Card className="company-card br-30px mb-120px">
      <Row gutter={[16, 16]} align="middle" justify="space-between" className="mb-20px">
        <Col>
          <Row align="bottom">
            <Title level={4} style={{ margin: 0 }}>
              Firma Listesi
            </Title>
            <Text type="secondary" className="ms-5" ><strong>({total})</strong> kayıt listelendi</Text>
          </Row>
        </Col>
        <Col>
          <Row justify="end" gutter={[16, 16]} >
            <Col>
              <Input
                allowClear
                placeholder="Ara"
                prefix={<SearchOutlined style={{ color: "#b8b9bc" }} />}
                className="d-input d-input-filter"
                style={{ width: "100%", maxWidth: 200, marginRight: 8 }}
                onChange={handleSearch}
              />
            </Col>
            <Col>
              <Button type="primary" onClick={showFilterDrawer}>
                Filtrele
              </Button>
            </Col>

            <Col>
              <Button
                type="primary"
                onClick={showDrawer}
                className="d-button dark-button width-auto br-30px"
              >
                Yeni Firma Ekle
              </Button>
            </Col>

            {(isAdmin || isCentralUser) && (
              <Col>
                <Tooltip title="Dışa Aktar">
                  <Dropdown menu={{ items: exportMenuItems }} trigger={['click']}>
                    <Button
                      loading={exporting}
                      style={{ height: 50, borderRadius: 50 }}
                      aria-label='Excel dışa aktar'
                    >
                      <FileExcelOutlined />
                      <DownOutlined />
                    </Button>
                  </Dropdown>
                </Tooltip>
              </Col>
            )}
          </Row>
        </Col>
      </Row>

      <Table
        loading={loading}
        scroll={{ x: 768 }}
        columns={columns}
        dataSource={filteredCompanies}
        rowKey="id"
        pagination={pagination}
        onChange={handleTableChange}
      />

      <ReminderDrawer
        open={reminderDrawerOpen}
        isEdit={false}
        isAdmin={isAdmin}
        companies={companies}
        proposals={[]}
        initialValues={reminderInitialValues}
        hideEntityTabs
        fixedCompanyId={reminderInitialValues?.companyId}
        fixedCompanyName={reminderCompanyName}
        onClose={() => setReminderDrawerOpen(false)}
        onSubmit={handleReminderSubmit}
      />

      {/* Şirket Oluştur / Güncelleme */}
      <CompanyCreate visible={isDrawerVisible} onClose={closeDrawer} />
      <Drawer
        title="Filtre"
        closable={{ 'aria-label': 'Close Button' }}
        onClose={onClose}
        open={open}
      >
        <Row gutter={[16, 16]}>
          {(isAdmin || isCentralUser) && (
            <Col span={24}>
              <Select
                showSearch
                allowClear
                placeholder="Kullanıcı"
                style={{ width: '100%' }}
                value={selectedCustomerId}
                onChange={handleCustomerChange}
                optionFilterProp="label"
                filterOption={(input, option: any) =>
                  normalizeTurkish(option?.label).includes(normalizeTurkish(input))
                }
              >
                {customerList.map((customer: any) => {
                  const label = `${customer?.firstName} ${customer?.lastName} - ${customer?.email}`;
                  return (
                    <Select.Option key={customer.id} value={customer.id} label={label}>
                      {label}
                    </Select.Option>
                  );
                })}
              </Select>
            </Col>
          )}

          <Col span={24}>
            <Select
              showSearch
              allowClear
              placeholder="İl"
              style={{ width: '100%' }}
              value={selectedCityId}
              onChange={handleCityChange}
              optionFilterProp="label"
              filterOption={(input, option: any) =>
                normalizeTurkish(option?.label).includes(normalizeTurkish(input))
              }
            >
              {cities
                ?.filter((city) => cityIdsToShow.includes(city.id))
                .map((city: any) => (
                  <Select.Option key={city.id} value={city.id} label={city.name}>
                    {city.name}
                  </Select.Option>
                ))}
            </Select>
          </Col>

          <Col span={24}>
            <Select
              showSearch
              allowClear
              placeholder="İlçe"
              style={{ width: '100%' }}
              value={selectedDistrictId}
              onChange={handleDistrictChange}
              optionFilterProp="label"
              filterOption={(input, option: any) =>
                normalizeTurkish(option?.label).includes(normalizeTurkish(input))
              }
            >
              {districts.map((district: any) => (
                <Select.Option key={district.id} value={district.id} label={district.name}>
                  {district.name}
                </Select.Option>
              ))}
            </Select>
          </Col>

        </Row>
        <Row gutter={[16, 16]} className="justify-content-end mt-16px">
          <Col>
            <Button
              type="default"
              onClick={() => filterClear()}
            >
              Temizle
            </Button>
          </Col>
          <Col>
            <Button type="primary" onClick={() => getFilterProposal()}>
              Filtrele
            </Button>
          </Col>
        </Row>
      </Drawer>
    </Card>
  );
};

export default Companies;
